import { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  SUBSCRIPTION_STATUS_STYLES,
  type SubscriptionStatus,
} from '@structural-climbing/ui'
import { PayPlanSheet } from '../payments/PayPlanSheet'
import type { PayablePlan } from '../payments/types'
import { SurfaceCard } from '../../design/SurfaceCard'
import { Skeleton } from '../../design/Skeleton'
import { fade, fadeRise } from '../../design/motion'
import type { MySubscription } from '../membership/types'
import { NextPlanLine } from '../membership/NextPlanLine'
import type { MyAddon } from '../membership/types'
import { CodeOverlay, memberCodePayload } from './CodeOverlay'

interface Props {
  subscription: MySubscription | null | undefined
  /** The renewal bought to follow the plan. */
  upcoming?: MySubscription | null
  gymId: string | null
  isCheckedIn: boolean
  checkedInAt: string | null
  gymTimezone: string | null
  isLastEntry?: boolean
  onCheckOut?: () => Promise<void>
  /**
   * What this gym sells online, empty unless it can actually charge. The list
   * being non-empty is the whole condition for offering to pay: the Worker
   * already weighed the gym's switch, Stripe's verdict and the plans' own flag
   * before answering.
   */
  payablePlans?: PayablePlan[]
  /** The sale was refused, so whatever we know about this gym is out of date. */
  onPaymentRefused?: () => void
  /**
   * The member paid a moment ago and the plan has not been written yet. The
   * offer to buy has to go away for those few seconds, or they can pay twice:
   * the server guard that refuses a second plan has nothing to refuse yet.
   */
  awaitingPlan?: boolean
  /** Extras held next to the plan, such as sessions with an instructor. */
  addons?: MyAddon[]
  /**
   * Opens the shared code overlay, which also holds the children's codes. Left
   * out, the card opens an overlay of its own with just the member's code.
   */
  onShowCode?: () => void
}

/**
 * The one thing above the fold.
 *
 * This is what the dashboard's eight equal-weight cards collapse into. It
 * merges the old MembershipCard and MemberQrCode, which was always an odd split
 * — your plan and your ability to get through the door are the same question,
 * and the answer changes shape depending on whether you are already inside.
 */
export function CheckInHero({
  subscription,
  upcoming = null,
  gymId,
  isCheckedIn,
  checkedInAt,
  gymTimezone,
  isLastEntry = false,
  onCheckOut,
  payablePlans = [],
  onPaymentRefused,
  awaitingPlan = false,
  addons = [],
  onShowCode,
}: Props) {
  const { user } = useAuth()
  const { t } = useTranslation()
  const userId = user?.sub as string | undefined

  const [fullscreen, setFullscreen] = useState(false)
  const [checkingOut, setCheckingOut] = useState(false)
  const [checkOutError, setCheckOutError] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [paying, setPaying] = useState(false)

  const payload = userId && gymId ? memberCodePayload(userId, gymId) : null
  const showCode = () => (onShowCode ? onShowCode() : setFullscreen(true))
  const ownOverlay = payload && !onShowCode && (
    <CodeOverlay
      people={[{ key: 'me', label: t('classes.me'), name: user?.name ?? '', payload }]}
      current={fullscreen ? 'me' : null}
      onSelect={() => {}}
      onClose={() => setFullscreen(false)}
    />
  )

  if (subscription === undefined) {
    return (
      <SurfaceCard elevated className="flex flex-col gap-4 p-6">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-12 w-40" />
        <Skeleton className="h-10 w-full rounded-sm" />
      </SurfaceCard>
    )
  }

  const isActive = subscription?.effective_status === 'active' || isCheckedIn

  // ---------------------------------------------------------------- checked in
  if (isCheckedIn) {
    const timeLabel = checkedInAt
      ? new Intl.DateTimeFormat(undefined, {
          timeZone: gymTimezone ?? undefined,
          hour: '2-digit',
          minute: '2-digit',
        }).format(new Date(checkedInAt))
      : null

    const handleCheckOut = async () => {
      if (!onCheckOut) return
      setCheckingOut(true)
      setCheckOutError(false)
      try {
        await onCheckOut()
      } catch {
        setCheckOutError(true)
      } finally {
        setCheckingOut(false)
      }
    }

    return (
      <>
        {/* One slim line once you are inside: Home belongs to the session, so
            the check-in shrinks to its status, the code and the way out. */}
        <SurfaceCard className="flex flex-col gap-1 border-success/30 bg-success/10 py-2 pl-3.5 pr-2">
          <div data-testid="checkin-strip" className="flex items-center gap-2.5">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
            </span>
            <p
              className="min-w-0 flex-1 truncate text-sm font-semibold"
              title={timeLabel ? t('checkin.checkedInAt', { time: timeLabel }) : undefined}
            >
              {t('checkin.inGym')} ·{' '}
              <span className="text-success">
                <ElapsedTimer since={checkedInAt} compact />
              </span>
            </p>
            {payload && (
              <Button
                isIconOnly
                size="sm"
                variant="flat"
                aria-label={t('checkin.myCode')}
                className="shrink-0 bg-content1"
                onPress={showCode}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                  <path d="M3 3h7v7H3zm2 2v3h3V5zM14 3h7v7h-7zm2 2v3h3V5zM3 14h7v7H3zm2 2v3h3v-3zM14 14h3v3h-3zm4 0h3v3h-3zm-4 4h3v3h-3zm4 0h3v3h-3z" />
                </svg>
              </Button>
            )}
            {onCheckOut && (
              <Button
                size="sm"
                variant="flat"
                className="shrink-0 bg-content1 font-semibold"
                isLoading={checkingOut}
                onPress={() => setShowConfirm(true)}
              >
                {t('checkin.leave')}
              </Button>
            )}
          </div>
          {checkOutError && <p className="text-xs text-danger">{t('checkin.checkOutError')}</p>}
        </SurfaceCard>
        {ownOverlay}

        <AnimatePresence>
          {showConfirm && (
            <motion.div
              variants={fade}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed inset-0 z-[var(--z-dialog)] flex items-center justify-center bg-black/50 px-6"
            >
              <motion.div
                variants={fadeRise}
                className="w-full max-w-sm rounded-lg bg-content1 p-6 shadow-e3"
              >
                <p className="mb-2 text-base font-semibold">{t('checkin.checkOutConfirmTitle')}</p>
                <p className="mb-6 text-sm text-foreground/60">
                  {isLastEntry
                    ? t('checkin.checkOutConfirmMessage')
                    : t('checkin.checkOutConfirmMessageSimple')}
                </p>
                <div className="flex gap-3">
                  <Button variant="flat" className="flex-1" onClick={() => setShowConfirm(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    color="danger"
                    className="flex-1"
                    isLoading={checkingOut}
                    onClick={() => {
                      setShowConfirm(false)
                      void handleCheckOut()
                    }}
                  >
                    {t('checkin.checkOutConfirmButton')}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    )
  }

  // ------------------------------------------------------------- no plan yet
  if (!subscription) {
    return (
      <SurfaceCard elevated className="px-6 py-7">
        <p className="eyebrow mb-2 text-foreground/35">{t('membership.sectionLabel')}</p>
        <p className="text-base font-semibold text-foreground/70">{t('membership.noActive')}</p>
        <p className="mt-1 text-sm text-foreground/45">{t('dashboard.noSubscription')}</p>
      </SurfaceCard>
    )
  }

  const {
    gym_plans: plan,
    effective_status,
    starts_at,
    expires_at,
    entries_remaining,
    frozen_until,
  } = subscription
  const notStarted = effective_status === 'scheduled'
  const frozen = effective_status === 'frozen'
  // Over, rather than merely not running yet: expired, used up or cancelled.
  const canBuy = payablePlans.length > 0 && !notStarted && !frozen && !awaitingPlan
  // The gym's calendar day: a plan starting at midnight there is stored as the
  // evening before in UTC.
  const startDate = new Date(starts_at).toLocaleDateString(undefined, {
    timeZone: gymTimezone ?? undefined,
  })
  // Same for the last frozen day, stored as the end of that day at the gym.
  const frozenUntilDate = frozen_until
    ? new Date(frozen_until).toLocaleDateString(undefined, {
        timeZone: gymTimezone ?? undefined,
      })
    : null

  const PLAN_TYPE_LABELS: Record<MySubscription['gym_plans']['plan_type'], string> = {
    day_pass: t('membership.typeDayPass'),
    entry_based: t('membership.typeEntryBased'),
    duration_based: t('membership.typeDurationBased'),
  }
  const STATUS_LABELS: Record<SubscriptionStatus, string> = {
    active: t('membership.statusActive'),
    scheduled: t('membership.statusScheduled'),
    frozen: t('membership.statusFrozen'),
    expired: t('membership.statusExpired'),
    depleted: t('membership.statusDepleted'),
    cancelled: t('membership.statusCancelled'),
  }

  const detail = [
    notStarted ? t('membership.startsOn', { date: startDate }) : null,
    frozen && frozenUntilDate ? t('membership.frozenUntil', { date: frozenUntilDate }) : null,
    expires_at
      ? t('membership.validUntil', { date: new Date(expires_at).toLocaleDateString() })
      : null,
    entries_remaining !== null
      ? t('membership.entriesRemaining', { count: entries_remaining })
      : null,
  ].filter(Boolean)

  return (
    <>
      <SurfaceCard elevated className="flex flex-col gap-4 p-4">
        {/* The plan in one line above the code. It used to be a tinted block of
            its own that pushed the code below the fold. */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-base font-semibold">{plan.name}</p>
            <p className="mt-0.5 text-xs text-foreground/55">
              {[PLAN_TYPE_LABELS[plan.plan_type], ...detail].join(' · ')}
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
              effective_status === 'active'
                ? 'bg-[var(--gym-primary)] text-[var(--gym-on-primary)]'
                : SUBSCRIPTION_STATUS_STYLES[effective_status]
            }`}
          >
            {STATUS_LABELS[effective_status]}
          </span>
        </div>

        {upcoming && (
          <NextPlanLine
            planName={upcoming.gym_plans.name}
            startsAt={upcoming.starts_at}
            discountPercent={upcoming.renewal_discount_percent}
            gymTimezone={gymTimezone}
          />
        )}

        {isActive && payload ? (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={showCode}
              aria-label={t('checkin.showToStaff')}
              className="rounded-lg border border-[var(--gym-line)] bg-white p-3 transition-transform [color-scheme:only_light] active:scale-[0.98]"
            >
              <QRCodeSVG value={payload} size={176} bgColor="#ffffff" fgColor="#000000" />
            </button>
            {/* Each half keeps to one line; on a narrow phone the button moves
                under the hint as a whole instead of the hint breaking mid-phrase. */}
            <div className="flex flex-wrap items-center justify-center gap-x-1.5 text-xs text-foreground/50">
              <span className="whitespace-nowrap">{t('checkin.showAtDesk')}</span>
              <span aria-hidden className="whitespace-nowrap">
                ·
              </span>
              <Button
                size="sm"
                variant="light"
                className="h-7 min-w-0 gap-1 px-1.5 text-xs font-bold text-[var(--gym-primary)]"
                onPress={showCode}
                startContent={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  >
                    <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
                  </svg>
                }
              >
                {t('checkin.fullScreen')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-foreground/55">
              {notStarted
                ? t('membership.notStartedYet', { date: startDate })
                : frozen && frozenUntilDate
                  ? t('membership.frozenNotice', { date: frozenUntilDate })
                  : awaitingPlan
                    ? t('payments.awaitingPlan')
                    : canBuy
                      ? t('payments.renewIntro')
                      : t('membership.contactGymPlan')}
            </p>
            {/* A plan that has not started, or one that is frozen, comes back on
                its own. Only a plan that is over is worth replacing, and saying
                "contact your gym" above a Pay button would contradict itself. */}
            {canBuy && (
              <Button
                className="w-full bg-[var(--gym-primary)] text-[var(--gym-on-primary)]"
                onClick={() => setPaying(true)}
              >
                {t('payments.buyButton')}
              </Button>
            )}
          </div>
        )}

        {addons.length > 0 && (
          <div
            role="list"
            className="flex flex-wrap items-center gap-1.5"
            aria-label={t('membership.addonsLabel')}
          >
            <span className="eyebrow text-foreground/40">{t('membership.addonsLabel')}</span>
            {addons.map((addon) => (
              <span
                role="listitem"
                key={addon.id}
                className="rounded-full bg-[var(--gym-veil)] px-2.5 py-0.5 text-xs text-foreground/75"
              >
                {addon.gym_plans.name}
                {addon.entries_remaining !== null &&
                  ` · ${t('membership.addonSessionsLeft', { count: addon.entries_remaining })}`}
              </span>
            ))}
          </div>
        )}
      </SurfaceCard>

      {canBuy && gymId && (
        <PayPlanSheet
          isOpen={paying}
          onClose={() => setPaying(false)}
          gymId={gymId}
          plans={payablePlans}
          onRefused={onPaymentRefused}
        />
      )}

      {ownOverlay}
    </>
  )
}

/**
 * How long you have been in the gym, ticking.
 *
 * Switches to h:mm:ss past an hour — a bouldering session runs long, and
 * "127:14" stops reading as a duration somewhere around the ninety-minute mark.
 */
function ElapsedTimer({ since, compact = false }: { since: string | null; compact?: boolean }) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!since) return
    // The compact form shows minutes only, so it need not tick every second.
    const id = setInterval(() => setNow(Date.now()), compact ? 15_000 : 1000)
    return () => clearInterval(id)
  }, [since, compact])

  if (!since) return null

  const elapsed = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000))
  const hours = Math.floor(elapsed / 3600)
  const minutes = Math.floor((elapsed % 3600) / 60)
  const seconds = elapsed % 60
  const pad = (n: number) => String(n).padStart(2, '0')

  // h:mm, the way a session is talked about: "one thirteen".
  if (compact) return <span className="tabular-nums">{`${hours}:${pad(minutes)}`}</span>

  return (
    <p className="shrink-0 text-xl font-bold tabular-nums leading-none tracking-tight text-success">
      {hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`}
    </p>
  )
}
